import { produce } from 'immer';
import { useCallback, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { SearchBar, useToast } from '@gbs-monorepo-packages/common';

import { CenteredText } from '../../components/CenteredText';
import { ContentPagination } from '../../components/ContentPagination';
import type { IApiThrowsError, IPaginationMetaProps } from '../../services/api';
import {
  type IClientDTO,
  type IPaginationCompaniesDTO,
  getCompanies,
} from '../../services/companies';
import Logger from '../../utils/logger';
import {
  ButtonUrl,
  ButtonsContent,
  ClientCard,
  ClientName,
  Header,
  Loading,
  LoadingContainer,
  MainContainer,
  MainContent,
  TitlePage,
} from './styles';

export const Companies = (): JSX.Element => {
  const { addToast } = useToast();
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const [clientsAux, setCompaniesAux] = useState<IClientDTO[]>([]);
  const [search, setSearch] = useState('');
  const lastSearch = useRef(search);
  const [paginationMeta, setPaginationMeta] =
    useState<IPaginationMetaProps | null>(null);

  const getCompaniesAux = useCallback(
    (page: number, limit: number) => {
      setLoadingCompanies(true);
      getCompanies({ page, limit, filter: search })
        .then((response: IPaginationCompaniesDTO) => {
          setCompaniesAux(response.data);
          setPaginationMeta(response.meta);
        })
        .catch((error: IApiThrowsError) => {
          Logger.debug('error: ', error);
        })
        .finally(() => {
          setLoadingCompanies(false);
        });
    },
    [search, addToast]
  );

  const searchCompanies = (searchByButton = false) => {
    if (!loadingCompanies || searchByButton) {
      setLoadingCompanies(true);
      const pageAux = searchByButton
        ? 0
        : Number((paginationMeta?.page ?? 0) > 0 ? paginationMeta?.page : 0);
      getCompanies({
        page: pageAux + 1,
        limit: 12,
        filter: search,
      })
        .then((response: IPaginationCompaniesDTO) => {
          setCompaniesAux(
            produce((draft) => {
              draft.push(...response.data);
            })
          );
          setPaginationMeta(response.meta);
        })
        .catch((error: IApiThrowsError) => {
          Logger.debug('error: ', error);
        })
        .finally(() => {
          setLoadingCompanies(false);
        });
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (search.trim() !== lastSearch.current) {
      const execSearch = () => {
        lastSearch.current = search.trim();
        setCompaniesAux([]);
        searchCompanies(true);
      };

      if (search.trim() === '') {
        execSearch();
      } else {
        timer = setTimeout(execSearch, 2000);
      }
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [search]);

  useEffect(() => {
    if (!clientsAux.length) {
      setLoadingCompanies(true);
      getCompaniesAux(1, 12);
    }
  }, []);

  return (
    <MainContainer data-cy="page-container">
      <Header>
        <TitlePage data-cy="title-pageName">Clients</TitlePage>
      </Header>

      <MainContent data-cy="list-card">
        <SearchBar
          search={search}
          onChange={(e) => {
            setSearch(e.target.value.slice(0, 50));
          }}
          onClearInput={() => {
            setSearch('');
          }}
          loading={loadingCompanies}
        />

        <ContentPagination dataCy="content-companies">
          {loadingCompanies && clientsAux.length === 0 ? (
            <LoadingContainer data-cy="loading-companies-container">
              <Loading dataCy="loading-companies" />
            </LoadingContainer>
          ) : clientsAux.length > 0 ? (
            <InfiniteScroll
              height={200}
              style={{
                overflow: 'auto',
                maxHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
              }}
              dataLength={clientsAux.length} // This is important field to render the next data
              next={searchCompanies}
              hasMore={clientsAux.length < (paginationMeta?.total ?? 0)}
              loader={
                <LoadingContainer data-cy="loading-teammates-container">
                  <Loading />
                </LoadingContainer>
              }
              endMessage={
                <CenteredText
                  message="You have seen it all"
                  dataCy="pagination-endLimit"
                />
              }
            >
              {clientsAux.map((client) => (
                <ClientCard key={client.id} data-cy={String(client.id)}>
                  <ClientName data-cy={client.name}>{client.name}</ClientName>
                  <ButtonsContent data-cy="info-container">
                    <ButtonUrl
                      data-cy={`buttonCourses-client${client.id}`}
                      to={`/companies/${client.id}/courses`}
                    >
                      Courses
                    </ButtonUrl>
                    <ButtonUrl
                      data-cy={`buttonDocuments-client${client.id}`}
                      to={`/companies/${client.id}/folder/public/`}
                    >
                      Resources
                    </ButtonUrl>
                    <ButtonUrl
                      data-cy={`buttonUsers-client${client.id}`}
                      to={`/companies/${client.id}/members`}
                    >
                      Users
                    </ButtonUrl>
                    <ButtonUrl
                      data-cy={`buttonTemplates-client${client.id}`}
                      to={`/companies/${client.id}/templates`}
                    >
                      Templates
                    </ButtonUrl>
                    <ButtonUrl
                      data-cy={`buttonAnalytics-client${client.id}`}
                      to={`/companies/${client.id}/analytics`}
                    >
                      Analytics
                    </ButtonUrl>
                  </ButtonsContent>
                </ClientCard>
              ))}
            </InfiniteScroll>
          ) : (
            <CenteredText
              message="No clients found"
              dataCy="text-noClients-found"
            />
          )}
        </ContentPagination>
      </MainContent>
    </MainContainer>
  );
};
